import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, XCircle } from "lucide-react";
import { api, getApiError } from "../../api/client";
import RejectModal from "./RejectModal";
import ServiceCard from "./Cards/ServiceCard";
import StatCard from "./Cards/StatCard";
import ConfidenceBreakdown from "./Cards/ConfidenceBreakdown";




function ServicesSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 px-1">
        <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500">Services are still being generated by the AI…</p>
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <div className="h-4 w-32 bg-gray-200 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded-full" />
            </div>
            <div className="h-6 w-24 bg-gray-100 rounded-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="h-3 w-full bg-gray-100 rounded" />
            <div className="h-3 w-4/5  bg-gray-100 rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-5 w-20 bg-gray-100 rounded" />
            <div className="h-5 w-24 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProposalReview({ run, onRefresh }) {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [services, setServices]               = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [approving, setApproving]             = useState(false);
  const [showReject, setShowReject]           = useState(
    () => !!location.state?.openRejectModal
  );
  const [error, setError]                     = useState("");

  const canReview    = run.status === "awaiting_review" || run.status === "ready_to_execute";
  const servicesReady = run.status !== "created" && run.status !== "analyzing";

  const fetchProposal = useCallback(async () => {
    try {
      const { data } = await api.get(`/golive/proposals/${run.id}`);
      const CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2 };
      const items = (data?.config?.items || []).slice().sort(
        (a, b) => (CONFIDENCE_ORDER[a.confidence] ?? 1) - (CONFIDENCE_ORDER[b.confidence] ?? 1)
      );
      setServices(items);
    } catch (err) {
      if (err?.response?.status === 404 && run.status !== "failed" && run.status !== "succeeded") {
        setTimeout(fetchProposal, 3000);
      }
    } finally {
      setLoadingServices(false);
    }
  }, [run.id, run.status]);

  useEffect(() => {
    if (!servicesReady) { setLoadingServices(false); return; }
    setLoadingServices(true);
    fetchProposal();
  }, [fetchProposal, servicesReady]);

  const handleApprove = async () => {
    setError("");
    setApproving(true);
    try {
      await api.post(`/golive/proposals/${run.id}/approve`);
      onRefresh();
      navigate(`/golive/${run.id}/script`);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setApproving(false);
    }
  };

  const META_CARDS = [
    { label: "Target",  value: run.target       },
    { label: "Adapter", value: run.adapter_name },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-4 flex-wrap">
        {META_CARDS.map(({ label, value }) => (
          <StatCard key={label} label={label} count={value} />
        ))}
      </div>

      {!servicesReady ? (
        <div className="gl-card">
          <div className="gl-empty">
            Analysis in progress — proposal will appear here once ready.
          </div>
        </div>
      ) : (
        <div className="flex gap-6 items-start">
          <div className="flex-1 flex flex-col gap-4">
            {error && <div className="gl-error">{error}</div>}
            {run.status === "generating_script" && (
              <div className="flex items-center gap-2 px-1">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
                <p className="text-sm text-slate-500">
                  Script is being generated — these services are locked in.
                </p>
              </div>
            )}

            {!loadingServices && (
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Proposed services
                </p>
                <p className="text-xs text-slate-400">{services.length} items</p>
              </div>
            )}

            {loadingServices
              ? <ServicesSkeleton />
              : services.map((service) => (
                  <ServiceCard key={service.name} service={service} />
                ))
            }

            {/* Action bar — visible when status allows review */}
            {!loadingServices && canReview && (
              <div className="flex items-center justify-between gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4 mt-2">
                <p className="text-xs text-slate-500 leading-relaxed max-w-sm">
                  {run.status === "ready_to_execute"
                    ? "A script has already been generated from this proposal."
                    : services.length === 0
                      ? "The AI didn't detect any services. Provide feedback to guide the re-analysis."
                      : "Approving will trigger script generation. Rejection sends structured feedback back to the AI for re-analysis."
                  }
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" className="gl-btn" onClick={() => setShowReject(true)}>
                    {services.length === 0 ? "Re-analyze with feedback" : "Edit items"}
                  </button>
                  {run.status !== "ready_to_execute" && services.length > 0 && (
                    <button
                      type="button"
                      className="gl-btn primary"
                      onClick={handleApprove}
                      disabled={approving}
                    >
                      <CheckCircle size={14} /> {approving ? "Approving…" : "Approve"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Confidence breakdown sidebar ── */}
          {!loadingServices && services.length > 0 && (
            <div className="w-72 shrink-0">
              <ConfidenceBreakdown />
            </div>
          )}

          {showReject && (
            <RejectModal
              runId={run.id}
              services={services}
              scriptGenerated={run.status === "ready_to_execute"}
              onClose={() => setShowReject(false)}
              onSuccess={() => { setShowReject(false); onRefresh(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}
